import { GenderInput } from "@/components/common/inputs/GenderInput";
import { ClaimantCreate, Policy, ResourceName, ValidAny, CLAIMANT_RELATIONSHIP_CHOICES } from "@/types";
import { getInputProps } from "@/utils/helpers/columnsHelper";
import {
  Box,
  Stack,
  Typography,
  Divider,
  MenuItem,
  alpha,
  Switch,
  FormControlLabel,
  TextField,
  Radio,
  RadioGroup,
  InputAdornment,
  Collapse,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  useGetOne,
  useGetList,
  ReferenceInput,
  AutocompleteInput,
  TextInput,
  DateInput,
  NumberInput,
  SelectInput,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { useAppTheme } from "@/hooks/useAppTheme";
import { formatYearRange, toISO, fromISO } from "@/utils/formatters/dateFormatter";
import { validateLaseredEffectiveDate } from "@/utils/validations/laseredClaimantValidation";
import { validateContractLaser } from "@/utils/validations/contractLaserValidation";
import { AutocompleteCodeInput } from "@/components/common/fields/AutocompleteCodeInput";
import { resolveConditionWindowDefaults } from "@/utils/helpers/conditionWindowDefaults";
import { STANDING_DEDUCTIBLE_HINT, createLaserKindOptions, type LaserKind } from "@/utils/helpers/laserKind";
import { draftToRequest, useLaseredConditionDrafts } from "@/resources/policies/hooks/useLaseredConditionDrafts";
import { LaseredConditionsEditor } from "@/resources/policies/components/LaseredConditionsEditor";

const getClaimantInputProps = (key: string, textReplace = {}) =>
  getInputProps(key, textReplace, ResourceName.CLAIMANTS);

// age calc from yyyy-mm-dd
const calcAge = (ymd?: string) => {
  if (!ymd) return undefined;
  const dob = new Date(ymd);
  if (isNaN(dob.getTime())) return undefined;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
  return age;
};

/** Build a display label for a policy in the selector */
const getPolicyLabel = (policy: Policy): string => {
  const vendorName =
    (policy as unknown as Record<string, string>).vendor_company_name ||
    policy.source_data?.stop_loss_carrier ||
    "No Carrier";
  const years = formatYearRange(policy.effective_start_date, policy.effective_end_date);
  return [vendorName, years].filter(Boolean).join(" · ");
};

// ─── Laser section component ─────────────────────────────────────────────────

interface LaserSectionProps {
  selectedPolicy?: Policy;
}

/**
 * "Mark as Lasered Claimant" on claimant create, with the laser types of the
 * laser dialog (PO 2026-10-07): Standard (a lasered deductible), Contract (a
 * contract laser or full exclusion) or Conditional (conditional lasers plus an
 * optional standing deductible). The claimant does not exist yet, so the laser
 * rides on the form as `_laser_data` / `_contract_laser_data` /
 * `_lasered_conditions` and is written after create (handleClaimantCreateSuccess).
 */
// Exported for unit testing of the laser-data form sync + Save-gating logic.
export const LaserSection = ({ selectedPolicy }: LaserSectionProps) => {
  const { theme } = useAppTheme();
  const { setValue, setError, clearErrors } = useFormContext();
  const [isLasered, setIsLasered] = useState(false);
  const [kind, setKind] = useState<LaserKind | "">("");
  const [amtLaseredDeductible, setAmtLaseredDeductible] = useState("");
  const [effectiveDate, setEffectiveDate] = useState<string>("");

  // Contract-laser / full-exclusion fields (Feature #5287).
  const [isExclusion, setIsExclusion] = useState(false);
  const [contractDeductible, setContractDeductible] = useState("");
  const [contractIncurredStart, setContractIncurredStart] = useState("");
  const [contractIncurredEnd, setContractIncurredEnd] = useState("");
  const [contractPaidStart, setContractPaidStart] = useState("");
  const [contractPaidEnd, setContractPaidEnd] = useState("");
  const [contractExcludedCpts, setContractExcludedCpts] = useState<string[]>([]);

  const selectedPolicyId = selectedPolicy?.id ? String(selectedPolicy.id) : "";
  const policyEffectiveStart = selectedPolicy?.effective_start_date;
  const policyEffectiveEnd = selectedPolicy?.effective_end_date;
  const specificBenefit = selectedPolicy?.specific_benefit;

  // Default the contract date windows from the policy's SpecificBenefit — all editable.
  const defaultContractDates = useMemo(
    () => ({
      incurredStart: specificBenefit?.eligible_expenses_incurred_start_date ?? "",
      incurredEnd: specificBenefit?.eligible_expenses_incurred_end_date ?? "",
      paidStart: specificBenefit?.eligible_expenses_paid_start_date ?? "",
      paidEnd: specificBenefit?.eligible_expenses_paid_end_date ?? "",
    }),
    [specificBenefit]
  );

  // Conditional lasers (Feature #3502, #7344). The claimant does not exist yet,
  // so rows are synced to `_lasered_conditions` and written after create.
  const conditionWindowDefaults = useMemo(
    () =>
      resolveConditionWindowDefaults({
        specificBenefit,
        policyStart: policyEffectiveStart,
        policyEnd: policyEffectiveEnd,
      }),
    [specificBenefit, policyEffectiveStart, policyEffectiveEnd]
  );
  const conditionDrafts = useLaseredConditionDrafts({
    policyId: selectedPolicyId,
    defaults: conditionWindowDefaults,
    active: isLasered && !!selectedPolicyId,
  });
  const conditionRows = conditionDrafts.drafts;
  const conditionsError = conditionDrafts.error;
  const conditional = isLasered && !!selectedPolicyId && kind === "conditional";
  useEffect(() => {
    if (conditional && conditionRows.length > 0) {
      setValue(
        "_lasered_conditions",
        { policy_id: selectedPolicyId, conditions: conditionRows.map(draftToRequest) },
        { shouldDirty: false }
      );
    } else {
      setValue("_lasered_conditions", undefined, { shouldDirty: false });
    }
    // At least one conditional laser: without one a Conditional claimant would be saved unlasered.
    const gate = conditional
      ? conditionsError || (conditionRows.length === 0 ? "Add at least one conditional laser." : "")
      : "";
    if (gate) {
      setError("_lasered_conditions", { type: "validate", message: gate });
    } else {
      clearErrors("_lasered_conditions");
    }
    return () => clearErrors("_lasered_conditions");
  }, [conditional, selectedPolicyId, conditionRows, conditionsError, setValue, setError, clearErrors]);

  // Whenever the selected policy changes, reset the effective_date to that policy's start.
  // This implements the "policy selection drives effective date" UX (bug #4535).
  useEffect(() => {
    setEffectiveDate(policyEffectiveStart ?? "");
  }, [selectedPolicyId, policyEffectiveStart]);

  // Pre-fill the contract windows from the selected policy's SpecificBenefit
  // when the policy changes (mirrors LaseredClaimantDialog create-mode defaults).
  useEffect(() => {
    setContractIncurredStart(defaultContractDates.incurredStart);
    setContractIncurredEnd(defaultContractDates.incurredEnd);
    setContractPaidStart(defaultContractDates.paidStart);
    setContractPaidEnd(defaultContractDates.paidEnd);
  }, [selectedPolicyId, defaultContractDates]);

  const contractLaserError =
    kind === "contract"
      ? validateContractLaser({
          isExcluded: isExclusion,
          incurredStart: contractIncurredStart,
          incurredEnd: contractIncurredEnd,
          paidStart: contractPaidStart,
          paidEnd: contractPaidEnd,
          deductible: contractDeductible,
        })
      : "";

  // The straight laser: Standard's lasered deductible, or Conditional's optional standing deductible.
  const amountSet = amtLaseredDeductible.trim() !== "";
  const amount = parseFloat(amtLaseredDeductible);
  const writesStraight = kind === "standard" || (kind === "conditional" && amountSet);
  const amountError =
    amountSet && !(amount > 0)
      ? kind === "conditional"
        ? "The lasered specific deductible must be greater than $0, or leave it blank."
        : "The lasered deductible must be greater than $0."
      : "";
  const effectiveDateError = writesStraight
    ? effectiveDate
      ? validateLaseredEffectiveDate({
          laserType: "individual",
          effectiveDate,
          policyStart: policyEffectiveStart,
          policyEnd: policyEffectiveEnd,
        })
      : "Enter the effective date."
    : "";

  // Sync laser data to form for post-create handling: the straight laser to
  // `_laser_data`, a contract laser / exclusion to `_contract_laser_data`.
  //
  // Also surface the active type's validation error to the parent SimpleForm via
  // setError so the Save button is gated when the user intends to laser but the
  // input is incomplete — otherwise the parent submits a claimant with no signal
  // that the laser was silently dropped.
  useEffect(() => {
    const contractKind = kind === "contract";

    // Register the error under the same form key as the data it guards. The key
    // name is incidental to gating — react-admin's Form blocks submit whenever
    // formState.errors is non-empty, regardless of which key holds the error.
    const gateKey = contractKind ? "_contract_laser_data" : "_laser_data";
    const otherKey = contractKind ? "_laser_data" : "_contract_laser_data";
    const gateError = !isLasered
      ? ""
      : !kind
        ? "Choose a laser type."
        : contractKind
          ? contractLaserError
          : amountError ||
            (kind === "standard" && !amountSet ? "Enter the lasered deductible." : "") ||
            effectiveDateError;
    if (gateError) {
      setError(gateKey, { type: "validate", message: gateError });
    } else {
      clearErrors(gateKey);
    }
    // Never leave a stale gate error on the inactive kind's key.
    clearErrors(otherKey);

    // Straight laser (Standard, or Conditional with a standing amount) → _laser_data
    if (isLasered && selectedPolicyId && writesStraight && amount > 0 && !effectiveDateError) {
      setValue(
        "_laser_data",
        {
          policy_id: selectedPolicyId,
          laser_type: "individual",
          amt_lasered_deductible: amount,
          amt_incremental_lasered_deductible: 0,
          effective_date: effectiveDate || undefined,
        },
        { shouldDirty: false }
      );
    } else {
      setValue("_laser_data", undefined, { shouldDirty: false });
    }

    // Contract laser / full exclusion → _contract_laser_data
    if (isLasered && contractKind && selectedPolicyId && !contractLaserError) {
      setValue(
        "_contract_laser_data",
        {
          policy_id: selectedPolicyId,
          is_contract_lasered: !isExclusion,
          is_contract_excluded: isExclusion,
          ...(isExclusion
            ? {}
            : {
                amt_contract_lasered_deductible: parseFloat(contractDeductible) || 0,
                contract_incurred_start_date: contractIncurredStart,
                contract_incurred_end_date: contractIncurredEnd,
                contract_paid_start_date: contractPaidStart,
                contract_paid_end_date: contractPaidEnd,
                excluded_procedures: contractExcludedCpts.map((s) => s.trim().toUpperCase()).filter(Boolean),
              }),
        },
        { shouldDirty: false }
      );
    } else {
      setValue("_contract_laser_data", undefined, { shouldDirty: false });
    }

    // Clear the gate error if this effect goes away (unmount or client/policy switch).
    return () => {
      clearErrors("_laser_data");
      clearErrors("_contract_laser_data");
    };
  }, [
    isLasered,
    selectedPolicyId,
    kind,
    amount,
    amountSet,
    amountError,
    writesStraight,
    effectiveDate,
    effectiveDateError,
    isExclusion,
    contractLaserError,
    contractDeductible,
    contractIncurredStart,
    contractIncurredEnd,
    contractPaidStart,
    contractPaidEnd,
    contractExcludedCpts,
    setValue,
    setError,
    clearErrors,
  ]);

  const straightFields = (label: string, required: boolean) => (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <TextField
        label={label}
        type="number"
        required={required}
        fullWidth
        value={amtLaseredDeductible}
        onChange={(e) => setAmtLaseredDeductible(e.target.value)}
        size="small"
        error={!!amountError}
        helperText={amountError || (required ? undefined : "Optional")}
        slotProps={{
          input: {
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          },
        }}
      />
      <TextField
        label="Effective Date"
        type="date"
        fullWidth
        required={required || amountSet}
        size="small"
        value={effectiveDate}
        onChange={(e) => setEffectiveDate(e.target.value)}
        error={!!effectiveDateError && (!!effectiveDate || amountSet)}
        helperText={
          (effectiveDate || amountSet ? effectiveDateError : "") ||
          (policyEffectiveStart && policyEffectiveEnd
            ? `Policy: ${policyEffectiveStart} to ${policyEffectiveEnd}`
            : undefined)
        }
        slotProps={{
          htmlInput: { min: policyEffectiveStart, max: policyEffectiveEnd },
          inputLabel: { shrink: true },
        }}
      />
    </Stack>
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <FormControlLabel
        control={
          <Switch
            checked={isLasered}
            onChange={(e) => {
              setIsLasered(e.target.checked);
              if (!e.target.checked) {
                setKind("");
                setAmtLaseredDeductible("");
                setIsExclusion(false);
                setContractDeductible("");
                setContractExcludedCpts([]);
              }
            }}
            size="small"
          />
        }
        label={
          <Typography variant="body2" fontWeight={500}>
            Mark as Lasered Claimant
          </Typography>
        }
      />

      <Collapse in={isLasered}>
        <Stack spacing={2} sx={{ mt: 2, pl: 1 }}>
          {!selectedPolicyId && (
            <Typography variant="body2" color="text.secondary">
              Select a policy above to configure laser details
            </Typography>
          )}

          {/* Laser type — radio cards */}
          {selectedPolicyId && (
            <>
              <Box>
                <Typography id="claimant-laser-type-label" variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Laser Type *
                </Typography>
                <RadioGroup
                  aria-labelledby="claimant-laser-type-label"
                  value={kind}
                  onChange={(e) => setKind(e.target.value as LaserKind)}
                >
                  <Stack spacing={1}>
                    {createLaserKindOptions().map((option) => {
                      const isSelected = kind === option.value;
                      return (
                        <Box
                          key={option.value}
                          onClick={() => setKind(option.value)}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 0.5,
                            p: 1.5,
                            borderRadius: 1.5,
                            cursor: "pointer",
                            border: isSelected
                              ? `2px solid ${theme.palette.primary.main}`
                              : `1px solid ${theme.palette.divider}`,
                            bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.04) : "transparent",
                            transition: "all 0.15s ease",
                            "&:hover": {
                              borderColor: isSelected ? theme.palette.primary.main : theme.palette.action.hover,
                            },
                          }}
                        >
                          <Radio
                            value={option.value}
                            checked={isSelected}
                            size="small"
                            sx={{ p: 0, mt: 0.25 }}
                            slotProps={{ input: { "aria-label": option.title } }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {option.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                              {option.description}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </RadioGroup>
              </Box>

              {/* Standard: the lasered deductible and its effective date */}
              {kind === "standard" && straightFields("Lasered Specific Deductible", true)}

              {/* Conditional: optional lasered specific deductible, then the conditional lasers */}
              {kind === "conditional" && (
                <>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                      {STANDING_DEDUCTIBLE_HINT}
                    </Typography>
                    {straightFields("Lasered Specific Deductible", false)}
                  </Box>
                  {/* Its own titled section: these codes change the deductible (UX review #3). */}
                  <LaseredConditionsEditor drafts={conditionDrafts} defaults={conditionWindowDefaults} />
                </>
              )}

              {/* Contract laser — four window dates, excluded CPTs, deductible;
                  or a full exclusion, which needs no terms. */}
              {kind === "contract" && (
                <Stack spacing={2}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch checked={isExclusion} onChange={(e) => setIsExclusion(e.target.checked)} size="small" />
                      }
                      label="Fully exclude this claimant from the contract"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {isExclusion
                        ? "The claimant is carved out of the contract entirely (no specific/aggregate eligibility). No terms are needed."
                        : "An elevated specific deductible scoped to a contract period. Only claims incurred AND paid inside both windows whose CPT is not excluded count toward it."}
                    </Typography>
                  </Box>
                  {!isExclusion && (
                    <>
                      <Stack direction="row" spacing={2}>
                        <TextField
                          label="Incurred Start"
                          type="date"
                          fullWidth
                          value={contractIncurredStart}
                          onChange={(e) => setContractIncurredStart(e.target.value)}
                          size="small"
                          slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                          label="Incurred End"
                          type="date"
                          fullWidth
                          value={contractIncurredEnd}
                          onChange={(e) => setContractIncurredEnd(e.target.value)}
                          size="small"
                          slotProps={{ inputLabel: { shrink: true } }}
                        />
                      </Stack>
                      <Stack direction="row" spacing={2}>
                        <TextField
                          label="Paid Start"
                          type="date"
                          fullWidth
                          value={contractPaidStart}
                          onChange={(e) => setContractPaidStart(e.target.value)}
                          size="small"
                          slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                          label="Paid End"
                          type="date"
                          fullWidth
                          value={contractPaidEnd}
                          onChange={(e) => setContractPaidEnd(e.target.value)}
                          size="small"
                          slotProps={{ inputLabel: { shrink: true } }}
                        />
                      </Stack>
                      <AutocompleteCodeInput
                        codeType="cpt"
                        value={contractExcludedCpts}
                        onChange={setContractExcludedCpts}
                        label="Excluded Procedures (CPT)"
                        placeholder="Search by code or description (e.g., office visit)"
                      />
                      <TextField
                        label="Contract-Lasered Deductible"
                        type="number"
                        required
                        fullWidth
                        value={contractDeductible}
                        onChange={(e) => setContractDeductible(e.target.value)}
                        size="small"
                        error={!!contractLaserError}
                        helperText={contractLaserError || undefined}
                        slotProps={{
                          input: {
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          },
                        }}
                      />
                    </>
                  )}
                </Stack>
              )}
            </>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

export const ClaimantCreateInputs = () => {
  const { setValue } = useFormContext<ClaimantCreate>();

  const clientId = useWatch({ name: "client_id_ref" });
  const dobISO = useWatch({ name: "claimant_birth_date" });
  const relationship = useWatch({ name: "claimant_relationship" });
  const isFamilyDeductible = useWatch({ name: "is_claimant_family_deductible" });

  const [selectedPolicyId, setSelectedPolicyId] = useState("");

  // partner auto-fill from selected client
  const { data: clientRecord } = useGetOne(ResourceName.CLIENTS, { id: clientId }, { enabled: !!clientId });

  useEffect(() => {
    if (clientRecord?.partner_id_ref) {
      setValue("partner_id_ref", clientRecord.partner_id_ref, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }, [clientRecord, setValue]);

  // Fetch policies for the selected client
  const { data: policies = [] } = useGetList<Policy>(
    ResourceName.POLICIES,
    {
      filter: clientId ? { client_id_ref: clientId } : {},
      pagination: { page: 1, perPage: 100 },
      sort: { field: "effective_start_date", order: "DESC" },
    },
    { enabled: !!clientId }
  );

  const activePolicies = useMemo(
    () => policies.filter((p) => p.status === "active" || p.status === "draft"),
    [policies]
  );

  // Auto-select the first active policy when policies load or client changes
  useEffect(() => {
    if (activePolicies.length > 0 && !selectedPolicyId) {
      setSelectedPolicyId(String(activePolicies[0].id));
    }
  }, [activePolicies, selectedPolicyId]);

  // Clear policy selection when client changes
  useEffect(() => {
    setSelectedPolicyId("");
  }, [clientId]);

  // Sync selected policy into form so the transform can inject it into policy_data
  useEffect(() => {
    setValue("_selected_policy_id" as keyof ClaimantCreate, (selectedPolicyId || undefined) as ValidAny, {
      shouldDirty: false,
    });
  }, [selectedPolicyId, setValue]);

  // derive age from DOB
  const ymd = useMemo(() => fromISO(dobISO), [dobISO]);
  useEffect(() => {
    const age = calcAge(ymd);
    setValue("claimant_age", typeof age === "number" ? age : undefined, {
      shouldDirty: true,
      shouldValidate: false,
    });
  }, [ymd, setValue]);

  return (
    <Box p={2}>
      <ReferenceInput source="client_id_ref" reference={ResourceName.CLIENTS} required>
        <AutocompleteInput {...getClaimantInputProps("client_id_ref")} isRequired size="small" />
      </ReferenceInput>

      {/* Policy selector — dependent on client, defaults to active policy */}
      {clientId && (
        <TextField
          label="Policy"
          select
          fullWidth
          size="small"
          value={selectedPolicyId}
          onChange={(e) => setSelectedPolicyId(e.target.value)}
          helperText={
            activePolicies.length === 0
              ? "No active policies found for this client"
              : "Associates this claimant with the selected policy year"
          }
          sx={{ mt: 1 }}
        >
          {activePolicies.map((policy) => (
            <MenuItem key={policy.id} value={policy.id}>
              {getPolicyLabel(policy)}
            </MenuItem>
          ))}
        </TextField>
      )}

      <TextInput
        {...getClaimantInputProps("member_id")}
        fullWidth
        size="small"
        helperText="External member/subscriber ID"
      />

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <TextInput {...getClaimantInputProps("claimant_first_name")} required fullWidth size="small" />
        <TextInput {...getClaimantInputProps("claimant_last_name")} required fullWidth size="small" />
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <GenderInput {...getClaimantInputProps("claimant_gender")} size="small" />
        <DateInput {...getClaimantInputProps("claimant_birth_date")} parse={toISO} format={fromISO} size="small" />
        <NumberInput {...getClaimantInputProps("claimant_age")} readOnly size="small" helperText="Auto-calculated" />
      </Stack>

      <SelectInput
        {...getClaimantInputProps("claimant_relationship")}
        choices={CLAIMANT_RELATIONSHIP_CHOICES}
        size="small"
        sx={{ mt: 2 }}
      />

      {(relationship === "spouse" || relationship === "dependent") && (
        <>
          <TextInput
            {...getClaimantInputProps("claimant_related_subscriber_id")}
            fullWidth
            size="small"
            sx={{ mt: 2 }}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <TextInput {...getClaimantInputProps("claimant_related_subscriber_first_name")} fullWidth size="small" />
            <TextInput {...getClaimantInputProps("claimant_related_subscriber_last_name")} fullWidth size="small" />
          </Stack>
        </>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={!!isFamilyDeductible}
            onChange={(e) =>
              setValue("is_claimant_family_deductible" as keyof ClaimantCreate, e.target.checked as ValidAny, {
                shouldDirty: true,
              })
            }
            size="small"
          />
        }
        label={
          <Typography variant="body2" fontWeight={500}>
            {getClaimantInputProps("is_claimant_family_deductible").label}
          </Typography>
        }
        sx={{ mt: 2 }}
      />

      {/* Laser section — only show when a client is selected */}
      {clientId && <LaserSection selectedPolicy={activePolicies.find((p) => String(p.id) === selectedPolicyId)} />}
    </Box>
  );
};
